<template>
  <div class="popup-wrapper" ref="popup">
    <transition name="fade">
      <div class="modal-dialog" :class="[calculatedClass, modalDialogClass]" :style="{width,height}">
        <div class="modal-content">
          <div v-if="showHeader" class="modal-header" :style="{'padding':headerPadding}" :class="modalHeaderClass">
            <div class="title-wrapper">
              <h2 class="modal-title">
                <slot name="title"></slot>
              </h2>
              <p>
                <slot name="subTitle"></slot>
              </p>
            </div>
            <div class="close-action">
              <button type="button" @click="closePopup" class="btn btn--ms btn--close" :class="closeClass">
                <LucideIcon icon="X"/>
              </button>
            </div>
          </div>

          <div class="modal-body scrollbar" :class="bodyClass" :style="{'padding': bodyPadding,'margin': bodyMargin}">
            <slot name="body"></slot>
          </div>
          <div class="modal-footer" v-if="showFooter">
            <button type="button" @click="closePopup" class="btn btn--ms btn--neutral-tertiary btn--regular">Close</button>
            <slot name="buttons"></slot>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import {defineComponent, type PropType} from 'vue'
import {PopupSize} from './common'
import {KeyboardKey} from "@shared/constants/common.constants";

export default defineComponent({
  name: 'PopupElement',
  emits: ['close'],
  props: {
    size: {
      type: String as PropType<PopupSize>,
      default: PopupSize.NORMAL
    },
    bodyPadding: {
      type: String,
      default: '1rem'
    },
    bodyWidth: {
      type: String,
      default: 'aut'
    },
    bodyMargin: {
      type: String,
      default: ''
    },
    headerPadding: {
      type: String,
      default: '1rem'
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    width: {
      type: [String],
      required: false
    },
    height: {
      type: [String],
      required: false
    },
    closeWithEsc: {
      type: Boolean,
      default: false
    },
    modalHeaderClass: {
      type: String,
      default: ''
    },
    modalDialogClass: {
      type: String,
      default: ''
    },
    bodyClass: {
      type: String,
      default: ''
    },
    closeClass: {
      type: String,
      default: ''
    }
  },
  computed: {
    calculatedClass(): Array<object> {
      return [
        {
          'modal-fullscreen': this.size === PopupSize.FULL_SCREEN
        }
      ]
    }
  },
  mounted() {
    if (this.closeWithEsc) {
      document.addEventListener('keyup', this.keyupFunction)
    }

    setTimeout(() => {
      document.body.classList.toggle('modal-open')
      return document.body.append(this.$refs.popup as Element)
    }, 0)
  },
  beforeUnmount() {
    setTimeout(() => {
      if (document.body.classList.contains('modal-open')) {
        document.body.classList.remove('modal-open')
        if (this.$refs.popup) {
          return document.body.removeChild(this.$refs.popup as Element)
        } else {
          const popup = document.querySelector('.popup-wrapper')
          if(popup) {
            return document.body.removeChild(popup as Element)
          }
        }
      }
    }, 0)

    if (this.closeWithEsc) {
      document.removeEventListener('keyup', this.keyupFunction)
    }
  },
  methods: {
    closePopup(): void {
      this.$emit('close')
    },

    keyupFunction(evt: any): void {
      if (evt.code === KeyboardKey.ESCAPE && !document.activeElement?.dataset?.ignoreKeyboardShortcutEvents) {
        this.closePopup()
      }
    }
  },

})
</script>

<style lang="scss" scoped>
.popup-wrapper {
  position: fixed;
  top: var(--space-0);
  left: var(--space-0);
  width: 100%;
  height: 100%;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.5);
  padding: var(--space-0);
  transition: opacity 250ms ease 0s;
  overflow-y: auto;

  .modal-dialog {
    min-width: 580px;
    max-width: 100%;
  }

  .modal-content {
    background-color: var(--light-100);
    border-radius: $br-lg;
    border: 0;
    animation: slideTop 0.4s cubic-bezier(0.215, 0.61, 0.355, 1) forwards;
    margin: 100px var(--space-0);;
  }

  .modal-header {
    text-align: left;
    border: 0;
    width: 100%;
    min-height: 90px;

    .title-wrapper {
      width: 100%;
    }

    p {
      margin-bottom: var(--space-0);
      color: var(--text-neutral);
    }

    .close-action {
      position: absolute;
      right: var(--space-0);
      top: var(--space-0);
      padding: var(--space-32);
    }
  }

  .modal-screen-header{
    background: var(--card-bg);
    border-left: 1px solid var(--card-border);
    position: fixed;
    width: 400px;
    right: var(--space-0);
    z-index: 101;
    padding: var(--space-8) !important;
    min-height: 54px;
    height: 54px;

    &.publish{
      @media (max-width: 968px) {
        width: 300px;
      }
      @media (max-width: 668px) {
        width: 100%;
        left: var(--space-0);
        right: initial;
        background-color: var(--light-100);
        border-bottom: 3px solid var(--accent-100);
      }
    }
    .close-action {
      position: relative;
      padding: 0;
      .btn-cancel{
        width: 40px;
        height: 40px;
        font-size: var(--fs-24);
      }
    }
  }

  &.edit-popup{
    .modal-content{
      border-radius: var(--shadow-sm) !important;
    }
    &.publish{
      .modal-fullscreen{
        width: auto;
        min-width: auto;
      }
    }
  }

  .modal-fullscreen {
    padding: 10px;
    .modal-content{
        margin: 0 !important;
        border-radius: $br-lg;
        overflow: hidden;
      }
      .modal-body {
        background-color: var(--layout-screen-bg);
        border-radius: var(--space-0) !important;
        overflow: auto !important;
        color: var(--text-dark);
        div{
          color: var(--text-dark);
        }
    }
  }


  .modal-body {
    text-align: left;
    color: var(--text-dark);

    p {
      font-size: var(--fs-24);
    }
  }

  .modal-footer {
    display: flex;
    gap: var(--space-8);
    padding: var(--space-0) var(--space-16) var(--space-16);
    border: var(--space-0);
  }

  .body-modal-screen{
    margin: 57px 400px var(--space-0) var(--space-0);
    @media (max-width: 968px) {
      margin: 57px 300px var(--space-0) var(--space-0);
    }
    @media (max-width: 668px) {
      margin: 57px var(--space-0) 207px var(--space-0);
    }
  }

}
.modal-onboarding{
 .modal-content{
   background-color: var(--modal-bg-dark);
  }
  .modal-title{
    font-weight: 700;
    font-size: var(--fs-32);
    background: var(--accent-linear);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-flex;
  }
  .modal-header .close-action{
    padding: var(--space-32);
  }
}

</style>
